import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import type {
	SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto,
	SmartField_Api_Contracts_Sensor_DimSensorDto,
	SmartField_Api_Contracts_Sensor_DimSensorPropertiesUpdateDto,
} from "../../api";
import { HeightHistoryChart } from "./HeightHistoryChart";
import { PlacementHistoryTimeline } from "./PlacementHistoryTimeline";
import { usePlacementHistoryForSensors } from "./usePlacementHistory";
import { nowDatetimeLocal, datetimeLocalToUtcIso } from "../../utils/dateUtils";
import { parseTowerId, towerChannels } from "./sensorConstants";

type Props = {
	towerName: string;
	trialId: number;
	blockId: number;
	els: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[];
	onSaveHeightsGroup: (
		sensorIds: number[],
		effectiveFrom: string,
		effectiveTo: string | null,
		data: SmartField_Api_Contracts_Sensor_DimSensorPropertiesUpdateDto,
	) => Promise<void>;
	canEdit: boolean;
	isSaving: boolean;
};


type GroupKey = "lower" | "upper" | "sonic";

type GroupState = {
	height: string;
	effectiveFrom: string;
	effectiveTo: string;
};

function initialGroupState(key: GroupKey, sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[]): GroupState {
	const first = sensors[0];
	return {
		height:
			first?.towerHeightInMeters != null
				? String(first.towerHeightInMeters)
				: localStorage.getItem(`tower-last-height-${key}`) ?? "",
		effectiveFrom: localStorage.getItem(`tower-last-eff-from-${key}`) ?? nowDatetimeLocal(),
		effectiveTo: localStorage.getItem(`tower-last-eff-to-${key}`) ?? "",
	};
}

export function TowerHeightConfigPanel({
	towerName,
	trialId,
	blockId,
	els,
	sensors,
	onSaveHeightsGroup,
	canEdit,
	isSaving,
}: Props) {
	const blockElIds = useMemo(() => {
		return new Set(
			els
				.filter((el) => el.blockId === blockId && el.fieldId === trialId)
				.map((el) => el.id),
		);
	}, [els, blockId, trialId]);

	const towerSensors = useMemo(
		() =>
			sensors.filter(
				(s) =>
					s.currentExperimentalLocationId != null &&
					blockElIds.has(s.currentExperimentalLocationId),
			),
		[sensors, blockElIds],
	);

	const towerId = parseTowerId(towerName);
	const channels = towerId != null ? towerChannels(towerId) : null;
	const miroSensors = useMemo(
		() => towerSensors.filter((s) => s.manufacturer === "Miro"),
		[towerSensors],
	);
	const lowerSensors = useMemo(
		() => miroSensors.filter((s) => s.channelNumber === channels?.lower),
		[miroSensors, channels?.lower],
	);
	const upperSensors = useMemo(
		() => miroSensors.filter((s) => s.channelNumber === channels?.upper),
		[miroSensors, channels?.upper],
	);
	const foreignMiroChannels = Array.from(
		new Set(
			miroSensors
				.map((s) => s.channelNumber)
				.filter((c) => c !== channels?.lower && c !== channels?.upper),
		),
	).sort((a, b) => (a ?? 0) - (b ?? 0));

	const sonicDevices = useMemo(
		() =>
			Array.from(
				new Map(
					towerSensors
						.filter((s) => s.manufacturer === "Metek")
						.map((s) => [s.deviceSerialKey, s]),
				).values(),
			),
		[towerSensors],
	);
	const [sonicSerialKey, setSonicSerialKey] = useState<string>(
		sonicDevices[0]?.deviceSerialKey ?? "",
	);
	const sonicSensors = useMemo(
		() => towerSensors.filter((s) => s.deviceSerialKey === sonicSerialKey),
		[towerSensors, sonicSerialKey],
	);

	const [lowerState, setLowerState] = useState<GroupState>(() => initialGroupState("lower", lowerSensors));
	const [upperState, setUpperState] = useState<GroupState>(() => initialGroupState("upper", upperSensors));
	const [sonicState, setSonicState] = useState<GroupState>(() => initialGroupState("sonic", sonicSensors));

	useEffect(() => {
		setSonicSerialKey(sonicDevices[0]?.deviceSerialKey ?? "");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blockId, trialId, sonicDevices[0]?.deviceSerialKey]);

	useEffect(() => {
		setLowerState(initialGroupState("lower", lowerSensors));
		setUpperState(initialGroupState("upper", upperSensors));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blockId, trialId, lowerSensors[0]?.id, upperSensors[0]?.id]);

	useEffect(() => {
		setSonicState(initialGroupState("sonic", sonicSensors));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blockId, trialId, sonicSerialKey, sonicSensors[0]?.id]);

	const [groupStatus, setGroupStatus] = useState<Record<GroupKey, "idle" | "success" | "error">>({
		lower: "idle",
		upper: "idle",
		sonic: "idle",
	});

	const historyQueries = {
		lower: usePlacementHistoryForSensors(lowerSensors),
		upper: usePlacementHistoryForSensors(upperSensors),
		sonic: usePlacementHistoryForSensors(sonicSensors),
	};

	const handleSaveGroup = async (key: GroupKey, groupSensors: SmartField_Api_Contracts_Sensor_DimSensorDto[], state: GroupState) => {
		if (groupSensors.length === 0) {
			return;
		}
		if (!state.height) {
			toast.error("Height is required");
			return;
		}
		setGroupStatus((prev) => ({ ...prev, [key]: "idle" }));
		try {
			await onSaveHeightsGroup(
				groupSensors.map((s) => s.id!),
				datetimeLocalToUtcIso(state.effectiveFrom),
				state.effectiveTo ? datetimeLocalToUtcIso(state.effectiveTo) : null,
				{
					towerHeightInMeters: parseFloat(state.height),
					chamberHeightInMeters: null,
					chamberExtensionInMeters: null,
					chamberExtensionIsOpaque: null,
				},
			);
			localStorage.setItem(`tower-last-height-${key}`, state.height);
			localStorage.setItem(`tower-last-eff-from-${key}`, state.effectiveFrom);
			localStorage.setItem(`tower-last-eff-to-${key}`, state.effectiveTo);
			setGroupStatus((prev) => ({ ...prev, [key]: "success" }));
		} catch {
			setGroupStatus((prev) => ({ ...prev, [key]: "error" }));
		}
	};

	const handleSaveAllHeights = async () => {
		await handleSaveGroup("lower", lowerSensors, lowerState);
		await handleSaveGroup("upper", upperSensors, upperState);
		await handleSaveGroup("sonic", sonicSensors, sonicState);
	};

	function renderGroupStatus(key: GroupKey) {
		const status = groupStatus[key];
		if (status === "success") {
			return <span className="text-xs text-green-600">Saved</span>;
		}
		if (status === "error") {
			return <span className="text-xs text-red-600">Failed — retry not automatic, re-run manually</span>;
		}
		return null;
	}

	function renderGroup(
		title: string,
		key: GroupKey,
		groupSensors: SmartField_Api_Contracts_Sensor_DimSensorDto[],
		state: GroupState,
		setState: (s: GroupState) => void,
	) {
		const isEmpty = groupSensors.length === 0;
		return (
			<div className="space-y-2 border border-gray-100 rounded p-3">
				<div className="flex items-center justify-between">
					<h5 className="text-xs font-semibold text-gray-600">{title}</h5>
					{renderGroupStatus(key)}
				</div>
				<p className="text-xs text-gray-400">
					{isEmpty
						? "0 sensors connected"
						: Array.from(
								new Map(
									groupSensors.map((s) => [
										s.deviceSerialKey,
										s.deviceFriendlyName ?? s.deviceSerialKey ?? `Channel ${s.channelNumber}`,
									]),
								).values(),
							).join(", ")}
				</p>
				<div>
					<label htmlFor={`${key}-height`} className="block text-xs font-medium text-gray-700 mb-1">Height (m)</label>
					<input
						id={`${key}-height`}
						type="number"
						step="0.01"
						value={state.height}
						onChange={(e) => setState({ ...state, height: e.target.value })}
						disabled={!canEdit || isEmpty}
						className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
					/>
				</div>
				<div className="grid grid-cols-2 gap-2">
					<div>
						<label htmlFor={`${key}-from`} className="block text-xs font-medium text-gray-700 mb-1">Effective from</label>
						<input
							id={`${key}-from`}
							type="datetime-local"
							value={state.effectiveFrom}
							onChange={(e) => setState({ ...state, effectiveFrom: e.target.value })}
							disabled={!canEdit || isEmpty}
							className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
						/>
					</div>
					<div>
						<label htmlFor={`${key}-to`} className="block text-xs font-medium text-gray-700 mb-1">Effective to</label>
						<input
							id={`${key}-to`}
							type="datetime-local"
							value={state.effectiveTo}
							onChange={(e) => setState({ ...state, effectiveTo: e.target.value })}
							disabled={!canEdit || isEmpty}
							className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
						/>
					</div>
				</div>
				{groupSensors.length > 0 && (
					<>
						{!historyQueries[key].isLoading && !historyQueries[key].isError && (
							<HeightHistoryChart
								entries={historyQueries[key].entries}
								field="tower"
							/>
						)}
						<PlacementHistoryTimeline
							entries={historyQueries[key].entries}
							els={els}
							field="tower"
							isLoading={historyQueries[key].isLoading}
							isError={historyQueries[key].isError}
						/>
					</>
				)}
			</div>
		);
	}

	return (
		<div className="space-y-3">
			{sonicDevices.length > 1 && (
				<div>
					<label htmlFor="sonic-device" className="block text-xs font-medium text-gray-700 mb-1">Sonic device</label>
					<select
						id="sonic-device"
						value={sonicSerialKey}
						onChange={(e) => setSonicSerialKey(e.target.value)}
						disabled={!canEdit}
						className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
					>
						{sonicDevices.map((s) => (
							<option key={s.deviceSerialKey} value={s.deviceSerialKey}>
								{s.deviceFriendlyName ?? s.deviceSerialKey}
							</option>
						))}
					</select>
				</div>
			)}

			{channels == null && (
				<p className="text-xs text-amber-700">
					Tower name "{towerName}" is not TOWER1–3, so its Miro channels are unknown. Rename it in the tower mapping.
				</p>
			)}
			{foreignMiroChannels.length > 0 && (
				<p className="text-xs text-amber-700">
					This block also has Miro channel(s) {foreignMiroChannels.join(", ")}, which belong to another tower.
					Re-place them on that tower's block; their heights can't be set here.
				</p>
			)}

			{renderGroup(`Lower inlet (channel ${channels?.lower ?? "?"})`, "lower", lowerSensors, lowerState, setLowerState)}
			{renderGroup(`Upper inlet (channel ${channels?.upper ?? "?"})`, "upper", upperSensors, upperState, setUpperState)}
			{renderGroup("Sonic", "sonic", sonicSensors, sonicState, setSonicState)}

			{canEdit && (lowerSensors.length > 0 || upperSensors.length > 0 || sonicSensors.length > 0) && (
				<>
				<p className="text-xs text-gray-400">Values above are prefilled from your last edit — review and click Save to apply.</p>
				<button
					type="button"
					onClick={handleSaveAllHeights}
					disabled={isSaving}
					className="px-3 py-1.5 text-sm bg-smartfield text-white rounded hover:bg-smartfield/90 disabled:opacity-50"
				>
					Save Heights
				</button>
				</>
			)}
		</div>
	);
}
